import React, { FC, useEffect, useState } from 'react';
import { nanoid } from 'nanoid';
import { Modal, FormInput } from '@praktio/common-ui';

import { cloneDeep } from 'lodash';
import SignupCode from './SignupCode';
import { Table } from '../common/Table/Table';

import './SignupCodes.scss';
import { Timezone } from '../../utils/constants';
import { notifySuccess } from '../../utils/functions';

const SignupHeaders = [
  {
    Header: 'Status',
    accessor: 'isActiveTableElement',
    className: 'SignupCodes-status',
  },
  {
    Header: 'Code',
    accessor: 'codeElement',
    className: 'SignupCodes-code',
    filterAccessor: 'code',
  },
  {
    Header: 'Redemptions',
    accessor: 'numOfRedemptions',
    className: 'SignupCodes-redemptions',
    Cell: (props: any) => {
      const original = props.row.original;
      return (
        <span>
          {original.numOfRedemptions}/{original.maxActivations}
        </span>
      );
    },
  },
  {
    Header: 'Expiration *',
    accessor: 'expirationString',
    className: 'SignupCodes-expiration',
  },
  {
    Header: '',
    accessor: 'options',
    className: 'SignupCodes-options',
  },
];
interface IModalProps {
  team: ITeam;
  show: boolean;
  onSave: (data: ISignupCode) => void;
  onHide: () => void;
  signupCode?: ISignupCode;
}

const generateNewSignupCode = (team: ITeam): ISignupCode => {
  const code = nanoid(12);
  const nextYear = new Date();
  nextYear.setFullYear(nextYear.getFullYear() + 1);
  return {
    _id: code,
    teamId: team._id,
    code,
    maxActivations: 10,
    expiration: nextYear,
    isActive: true,
    isNew: true,
  };
};

const dateToInputString = (value: string | Date) => {
  const date = new Date(value);
  const year = date.getFullYear();

  // Month is 0-based, so add 1
  let month = (date.getMonth() + 1).toString();
  let day = date.getDate().toString();

  // Prepend a zero if the month is a single digit
  if (month.length === 1) {
    month = `0${month}`;
  }

  // Prepend a zero if the day is a single digit
  if (day.length === 1) {
    day = `0${day}`;
  }

  return `${year}-${month}-${day}`;
};

const AddUpdateSignupCodeModal: FC<IModalProps> = ({ team, signupCode, show, onSave, onHide }) => {
  const [data, setData] = useState<ISignupCode>(signupCode || generateNewSignupCode(team));

  useEffect(() => {
    setData(generateNewSignupCode(team));
  }, [team]);

  useEffect(() => {
    setData(signupCode || generateNewSignupCode(team));
  }, [signupCode, team]);

  const onChange = (value: any, prop: string) => {
    if (prop) {
      const updatedData: ISignupCode = cloneDeep(data);
      updatedData[prop] = value;
      setData(updatedData);
    }
  };

  const onExpirationChange = (e: FormEvent) => {
    const value = e.target.value;
    const timezoneOffset = new Date().getTimezoneOffset();
    const adjustedDate = new Date(value);
    adjustedDate.setMinutes(timezoneOffset);
    const updatedData: ISignupCode = cloneDeep(data);
    updatedData.expiration = adjustedDate;
    setData(updatedData);
  };

  const expiration = data.expiration ? dateToInputString(data.expiration) : '';
  return (
    <Modal
      title={`Add Signup Code for team '${team.name}'`}
      onSave={() => onSave(data)}
      onHide={onHide}
      show={show}
      saveText="APPLY"
    >
      {show && (
        <form>
          <FormInput
            label="Code"
            onChange={(e: FormEvent) => onChange(e.target.value, 'code')}
            value={data.code}
            fieldName="code"
          />
          <FormInput
            label="Max Activations"
            type="number"
            onChange={(e: FormEvent) => onChange(e.target.value, 'maxActivations')}
            value={data.maxActivations}
            fieldName="maxActivations"
          />
          <FormInput
            label="Expiration"
            type="date"
            onChange={onExpirationChange}
            value={expiration}
            fieldName="expiration"
            subtext={`Based on your local time zone (${Timezone})`}
          />
          <FormInput
            label="Active"
            type="switch"
            onChange={() => onChange(!data.isActive, 'isActive')}
            value={data.isActive}
            fieldName="isActive"
          />
        </form>
      )}
    </Modal>
  );
};

interface IProps {
  team: ITeam;
  signupCodes?: ISignupCode[];
  showAddSignupCodeModal: boolean;
  setShowAddSignupCodeModal: React.Dispatch<React.SetStateAction<boolean>>;
  onApply: (data: ISignupCode, isDelete?: boolean) => void;
}

const SignupCodes: FC<IProps> = ({
  team,
  signupCodes,
  onApply,
  showAddSignupCodeModal,
  setShowAddSignupCodeModal,
}) => {
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [signupCode, setSignupCode] = useState<ISignupCode | undefined>();

  const formattedSignupCodeData = (signupCodes?: ISignupCode[]) => {
    const updatedCodes = cloneDeep(signupCodes);
    updatedCodes?.forEach((code, index) => {
      const expiration = new Date(code.expiration);
      if (expiration instanceof Date) {
        code.expirationString = expiration.toLocaleDateString();
      }

      code.isActiveTableElement = (
        <FormInput
          type="switch"
          fullWidth
          fieldName="isActive"
          label={code.isActive ? 'On' : 'Off'}
          value={code.isActive}
          largeSwitch
          onChange={() => {
            code.isActive = !code.isActive;
            onApply(code);
          }}
        />
      );

      code.codeElement = (
        <div className="signup-code-code">
          {code.code}
          <span
            className="material-icons"
            onClick={() => {
              navigator.clipboard.writeText(code.code);
              notifySuccess('Code copied!');
            }}
          >
            content_copy
          </span>
          <span
            className="material-icons"
            onClick={() => {
              navigator.clipboard.writeText(`${window.location.origin}/signup?code=${code.code}`);
              notifySuccess('Signup link with code copied!');
            }}
          >
            link
          </span>
        </div>
      ) as any;
      const numOfRedemptions = code.redemptions ? code.redemptions?.length : 0;
      code.numOfRedemptions = numOfRedemptions;

      code.options = (
        <div className="signup-code-options d-flex">
          {/* Info Button */}
          <div
            className="signup-code-option"
            onClick={() => {
              setSignupCode(code);
              setShowDetailsModal(true);
              setShowAddSignupCodeModal(false);
            }}
          >
            <span className="material-icons">info</span>
          </div>
          {/* Edit Button */}
          <div
            className="signup-code-option"
            onClick={() => {
              setSignupCode(code);
              setShowAddSignupCodeModal(true);
              setShowDetailsModal(false);
            }}
          >
            <span className="material-icons">edit</span>
          </div>
          {/* Delete Button */}
          <div
            className="signup-code-option"
            onClick={() => {
              onApply(updatedCodes[index], true);
            }}
          >
            <span className="material-icons">delete</span>
          </div>
        </div>
      );
    });
    return updatedCodes;
  };

  return (
    <>
      {signupCodes && signupCodes?.length > 0 && (
        <Table columns={SignupHeaders} data={formattedSignupCodeData(signupCodes) || []} />
      )}
      <AddUpdateSignupCodeModal
        team={team}
        show={showAddSignupCodeModal}
        signupCode={signupCode}
        onSave={(data) => {
          setShowAddSignupCodeModal(false);
          onApply(data);
          setSignupCode(generateNewSignupCode(team));
        }}
        onHide={() => {
          setShowAddSignupCodeModal(false);
          setSignupCode(generateNewSignupCode(team));
        }}
      />
      <Modal
        title={`Signup Code '${signupCode?.code}'`}
        onSave={() => {}}
        onHide={() => {
          setSignupCode(undefined);
          setShowDetailsModal(false);
        }}
        show={showDetailsModal}
        saveText="OK"
      >
        <SignupCode signupCode={signupCode} />
      </Modal>
    </>
  );
};

export default SignupCodes;
